import { isNil, range } from 'lodash';
import createCachedSelector from 're-reselect';
import Shift from 'resources/Shift';

import { containsShiftsInCurrentLocation } from 'features/scheduleBuilder/ScheduleBuilderView/utils/containsShiftsInCurrentLocation';
import shiftSortCompare from 'features/scheduleBuilder/ScheduleBuilderView/utils/shiftSortCompare';
import { selectBlackoutOnDate } from 'features/scheduleBuilder/slices/timeOffBlackoutPeriods/slice';
import { isOvernight } from 'features/scheduleBuilder/util';

import { dateFromTime } from 'util/dateTime';

import {
  adjustOvernightClosingTime,
  formatCollection,
  fullDayPeriod,
  getEarliestStartTime,
  getLatestEndTime,
  getOvernightShiftHour,
} from '../utils/dayViewData';

import { selectEventsForDay } from './events';
import {
  selectClosingTime,
  selectIsAvailabilitiesVisible,
  selectIsTimeOffsVisible,
  selectJobIdsForCurrentLocation,
  selectOpenTime,
} from './index';
import { selectOpenShiftsSortedOnDate } from './openShifts';
import { selectScheduleAvailabilitiesForJobOnDate } from './scheduleAvailability';
import { selectAllShiftsOnDate } from './shift';
import { selectTimeOffsForJobOnDate } from './timeOff';

export const selectShiftsForDay = createCachedSelector(
  Shift.selectAllEntities,
  (_, { date }) => date,
  (shifts, date) =>
    shifts
      .filter(s => dateFromTime(s.attributes.startAt) === date)
      .sort(shiftSortCompare)
)((_, props) => `dayView-selectShiftsForDay-${props.date}`);

export const selectOvernightShiftsForDay = createCachedSelector(
  selectShiftsForDay,
  shifts =>
    shifts
      .filter(s => isOvernight(s.attributes.startAt, s.attributes.endAt))
      .sort(shiftSortCompare)
)((_, props) => `dayView-selectOvernightShiftsForDay-${props.date}`);

export const selectStartHour = createCachedSelector(
  state => selectOpenTime(state),
  (state, props) => selectShiftsForDay(state, props),
  (openTime, shifts) => {
    const earliestShiftStartTime = getEarliestStartTime(shifts);

    if (earliestShiftStartTime) {
      const shiftStartHour = new Date(earliestShiftStartTime).getHours();
      if (shiftStartHour < openTime) return shiftStartHour;
    }

    return openTime;
  }
)((_, props) => `dayView-selectStartHour-${props.date}`);

export const selectEndHour = createCachedSelector(
  state => selectOpenTime(state),
  state => selectClosingTime(state),
  (state, props) => selectShiftsForDay(state, props),
  (openingTime, closingTime, shifts) => {
    const overnightBusinessHours = closingTime < openingTime;

    if (overnightBusinessHours) {
      closingTime = adjustOvernightClosingTime(closingTime);
    }
    const lastShiftTime = getLatestEndTime(shifts);

    if (lastShiftTime) {
      const lastShiftStartTime = shifts[0]?.attributes?.startAt;
      const lastShiftIsOvernight = isOvernight(
        lastShiftStartTime,
        lastShiftTime
      );
      const shiftEndDate = new Date(lastShiftTime);
      let shiftEndHour = shiftEndDate.getHours();
      if (shiftEndDate.getMinutes() > 0) {
        shiftEndHour++;
      }

      if (lastShiftIsOvernight) {
        shiftEndHour = getOvernightShiftHour(shiftEndHour);
      }

      if (shiftEndHour > closingTime) return shiftEndHour;
    }

    return closingTime;
  }
)((_, props) => `dayView-selectEndHour-${props.date}`);

export const selectBusinessHours = createCachedSelector(
  selectStartHour,
  selectEndHour,
  (businessOpenTime, businessClosingTime) => {
    if (isNil(businessOpenTime) || isNil(businessClosingTime)) {
      return [];
    }

    return range(businessOpenTime, businessClosingTime);
  }
)((_, props) => `dayView-selectBusinessHours-${props.date}`);

export const selectEmployeeSquares = createCachedSelector(
  selectJobIdsForCurrentLocation,
  selectAllShiftsOnDate,
  selectTimeOffsForJobOnDate,
  selectScheduleAvailabilitiesForJobOnDate,
  selectIsTimeOffsVisible,
  selectIsAvailabilitiesVisible,
  selectStartHour,
  selectEndHour,
  (
    jobIds,
    shifts,
    timeOffs,
    unavailabilities,
    isTimeOffsVisible,
    isAvailabilitiesVisible,
    openTime,
    closingTime
  ) =>
    formatCollection(
      [
        ...shifts.map(shift =>
          fullDayPeriod({
            id: shift.id,
            type: containsShiftsInCurrentLocation(shift, jobIds)
              ? shift.type
              : 'multiLocationShift',
            startAt: shift.attributes.startAt,
            endAt: shift.attributes.endAt,
          })
        ),
        ...(isTimeOffsVisible ? timeOffs : []).map(t =>
          fullDayPeriod({
            id: t.id,
            type: t.type,
            startAt: t.startAt,
            endAt: t.endAt,
            isFullDay: t.isWholeDay,
            openTime,
            closingTime,
          })
        ),
        ...(isAvailabilitiesVisible ? unavailabilities : [])
          .filter(a => a.title === 'Unavailable')
          .map(a =>
            fullDayPeriod({
              id: a.id,
              type: a.type,
              startAt: a.startAt,
              endAt: a.endAt,
              isFullDay: a.allDay,
              openTime,
              closingTime,
            })
          ),
      ],
      openTime,
      closingTime,
      true
    )
)((_, props) => `dayView-selectEmployeeSquares-${props.userId}-${props.date}`);

export const selectOpenShifts = createCachedSelector(
  selectOpenShiftsSortedOnDate,
  selectStartHour,
  selectClosingTime,
  (shifts, startHour, closingTime) =>
    formatCollection(
      shifts.map(s =>
        fullDayPeriod({
          id: s.id,
          type: 'openShift',
          startAt: s.attributes.startAt,
          endAt: s.attributes.endAt,
        })
      ),
      startHour,
      closingTime,
      false
    )
)((_, props) => `dayView-selectOpenShifts-${props.jobId}-${props.date}`);

export const selectEvents = createCachedSelector(
  selectEventsForDay,
  selectOpenTime,
  selectEndHour,
  (events, openTime, closingTime) =>
    formatCollection(
      events.map(e =>
        fullDayPeriod({
          id: e.id,
          type: 'event',
          isFullDay: true,
          openTime,
          closingTime,
        })
      ),
      openTime,
      closingTime,
      false
    )
)((_, props) => `dayView-selectEvents-${props.jobId}-${props.date}`);

export const selectBlackouts = createCachedSelector(
  selectBlackoutOnDate,
  selectOpenTime,
  selectEndHour,
  (blackoutId, openTime, closingTime) => {
    if (blackoutId) {
      return formatCollection(
        [
          fullDayPeriod({
            id: blackoutId,
            type: 'timeOffBlackout',
            isFullDay: true,
            openTime,
            closingTime,
          }),
        ],
        openTime,
        closingTime,
        false
      );
    }

    return [];
  }
)((_, props) => `dayView-selectBlackouts-${props.jobId}-${props.date}`);

export const selectPreferredTimeSquares = createCachedSelector(
  selectScheduleAvailabilitiesForJobOnDate,
  selectIsAvailabilitiesVisible,
  selectStartHour,
  selectEndHour,
  (availabilities, isAvailabilitiesVisible, openTime, closingTime) =>
    formatCollection(
      [
        ...(isAvailabilitiesVisible ? availabilities : [])
          .filter(a => a.title === 'Preferred')
          .map(a =>
            fullDayPeriod({
              id: a.id,
              type: 'preferred',
              startAt: a.startAt,
              endAt: a.endAt,
              isFullDay: a.allDay,
              openTime,
              closingTime,
            })
          ),
      ],
      openTime,
      closingTime,
      true
    )
)(
  (_, props) =>
    `dayView-selectPreferredTimeSquares-${props.jobId}-${props.date}`
);
